import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, Table as DocxTable, TableRow as DocxTableRow, TableCell as DocxTableCell, WidthType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';
import { 
  FileText, Download, Calendar, Printer, FileSpreadsheet,
  TrendingUp, AlertTriangle, ClipboardList, Activity, FileDown, Loader2,
  FileType, Clock, Settings, RefreshCw, Play
} from 'lucide-react';
import { PageHeader, MetricCard, SectionHeader, ModernCard, modernTabsClass, modernTabTriggerClass } from '@/components/ModernUI';
import { InsightsExport } from '@/components/InsightsExport';
import { EnhancedReportSection } from '@/components/EnhancedReportSection';
import { ReportTimeframeSelector } from '@/components/ReportTimeframeSelector';
import { ScheduledReportManager } from '@/components/ScheduledReportManager';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ReportSection = ({ title, icon: Icon, children }) => (
  <div className="mb-8 print:mb-4">
    <div className="flex items-center gap-2 mb-4 pb-2 border-b">
      <Icon className="h-5 w-5 text-primary" />
      <h2 className="text-lg font-semibold">{title}</h2>
    </div>
    {children}
  </div>
);

const MetricRow = ({ label, value, target, status }) => {
  const getStatusColor = (s) => {
    if (s === 'good') return 'text-green-600';
    if (s === 'warning') return 'text-yellow-600';
    if (s === 'danger') return 'text-red-600';
    return '';
  };

  return (
    <TableRow>
      <TableCell className="font-medium">{label}</TableCell>
      <TableCell className={getStatusColor(status)}>{value ?? '—'}</TableCell>
      <TableCell className="text-muted-foreground">{target ?? '—'}</TableCell>
    </TableRow>
  );
};

export default function Reports() {
  const { token, user } = useAuth();
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [exportingWord, setExportingWord] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [scheduledConfigs, setScheduledConfigs] = useState([]);
  const [generatedReports, setGeneratedReports] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [newSchedule, setNewSchedule] = useState({
    report_type: 'monthly',
    day_of_month: 1,
    format: 'pdf',
    enabled: true
  });
  const reportRef = useRef(null);

  const canConfigureSystem = user?.role === 'super_admin' || user?.role === 'admin' || user?.role === 'manager';

  const fetchReportData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/qapi-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setReportData(data);
      }
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const fetchScheduledConfigs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/scheduled/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setScheduledConfigs(data);
      }
    } catch (error) {
      console.error('Failed to load scheduled configs');
    }
  };

  const fetchGeneratedReports = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/generated?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setGeneratedReports(data);
      }
    } catch (error) {
      console.error('Failed to load generated reports');
    }
  };

  useEffect(() => {
    fetchReportData();
    fetchScheduledConfigs();
    fetchGeneratedReports();
  }, [token]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/export/csv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `qapi_report_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('CSV exported successfully');
      }
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const exportPDF = async () => {
    setExportingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPos = margin;

      // Header
      pdf.setFontSize(20);
      pdf.setTextColor(20, 184, 166); // Teal color
      pdf.text('QAPI Summary Report', margin, yPos);
      yPos += 10;

      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated: ${new Date(reportData?.generated_at).toLocaleString()}`, margin, yPos);
      pdf.text(`By: ${reportData?.generated_by || 'System'}`, margin + 100, yPos);
      yPos += 10;

      // Divider line
      pdf.setDrawColor(20, 184, 166);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      // Helper function to add section
      const addSection = (title, data, fields) => {
        if (yPos > pageHeight - 40) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(50, 50, 50);
        pdf.text(title, margin, yPos);
        yPos += 8;

        pdf.setFontSize(9);
        pdf.setTextColor(80, 80, 80);

        fields.forEach(({ label, value, target }) => {
          if (yPos > pageHeight - 20) {
            pdf.addPage();
            yPos = margin;
          }
          
          const displayValue = value !== undefined && value !== null ? String(value) : '—';
          const targetText = target ? ` (Target: ${target})` : '';
          pdf.text(`${label}: ${displayValue}${targetText}`, margin + 5, yPos);
          yPos += 5;
        });
        
        yPos += 8;
      };

      // Program Overview Section
      if (latestProgram) {
        addSection('Program Overview', latestProgram, [
          { label: 'Average Daily Census (ADC)', value: latestProgram.adc?.toFixed(1) },
          { label: 'Total Admissions', value: latestProgram.total_admissions },
          { label: 'Live Discharges', value: `${latestProgram.live_discharges_percent?.toFixed(1)}%` },
          { label: 'Revocations', value: `${latestProgram.revocations_percent?.toFixed(1)}%`, target: '<5%' },
          { label: 'Average Length of Stay', value: `${latestProgram.alos?.toFixed(1)} days` },
          { label: 'CAHPS Overall Score', value: latestProgram.cahps_overall_score?.toFixed(1), target: '≥85' },
          { label: 'Open Complaints', value: latestProgram.open_complaints, target: '0' },
        ]);
      }

      // Clinical Quality Section
      if (latestClinical) {
        addSection('Clinical Quality Metrics', latestClinical, [
          { label: 'Pain Controlled Within 48h', value: `${latestClinical.pain_controlled_48h_percent?.toFixed(1)}%`, target: '≥95%' },
          { label: 'Dyspnea Improvement Within 48h', value: `${latestClinical.dyspnea_improvement_48h_percent?.toFixed(1)}%`, target: '≥90%' },
          { label: 'Missed Visit Rate', value: `${latestClinical.missed_visit_rate_percent?.toFixed(1)}%`, target: '<5%' },
          { label: 'Visits Completed vs Planned', value: `${latestClinical.visits_completed_vs_planned_percent?.toFixed(1)}%`, target: '≥95%' },
          { label: 'HOPE Completed On Time', value: `${latestClinical.hope_completed_on_time_percent?.toFixed(1)}%`, target: '≥95%' },
          { label: 'Timely IDG Completion', value: `${latestClinical.timely_idg_completion_percent?.toFixed(1)}%`, target: '100%' },
        ]);
      }

      // Safety & Incidents Section
      if (latestSafety) {
        addSection('Safety & Incidents', latestSafety, [
          { label: 'Falls with Injury', value: latestSafety.falls_with_injury, target: '0' },
          { label: 'Falls without Injury', value: latestSafety.falls_without_injury },
          { label: 'Medication Errors (Actual Harm)', value: latestSafety.medication_errors_actual_harm, target: '0' },
          { label: 'Medication Errors (Near Miss)', value: latestSafety.medication_errors_near_miss },
          { label: 'Hospitalizations', value: latestSafety.hospitalizations },
          { label: 'ER Visits', value: latestSafety.er_visits },
          { label: 'RCA Completed', value: `${latestSafety.rca_completed_percent?.toFixed(1)}%`, target: '100%' },
        ]);
      }

      // Regulatory Compliance Section
      if (latestRegulatory) {
        addSection('Regulatory Compliance', latestRegulatory, [
          { label: 'CTI Timeliness', value: `${latestRegulatory.cti_timeliness_percent?.toFixed(1)}%`, target: '100%' },
          { label: 'F2F Correct Window', value: `${latestRegulatory.f2f_correct_window_percent?.toFixed(1)}%`, target: '100%' },
          { label: 'POC Updates On Time', value: `${latestRegulatory.poc_updates_on_time_percent?.toFixed(1)}%`, target: '≥98%' },
          { label: 'Orders Signed On Time', value: `${latestRegulatory.orders_signed_on_time_percent?.toFixed(1)}%`, target: '≥95%' },
          { label: 'Documentation Completeness', value: `${latestRegulatory.documentation_completeness_score?.toFixed(1)}%`, target: '≥95%' },
          { label: 'ADR Favorable', value: latestRegulatory.adr_favorable },
          { label: 'ADR Unfavorable', value: latestRegulatory.adr_unfavorable, target: '0' },
        ]);
      }

      // Patient Experience Section
      if (latestPatient) {
        addSection('Patient Experience (CAHPS)', latestPatient, [
          { label: 'Communication', value: latestPatient.cahps_communication, target: '≥85' },
          { label: 'Pain & Symptom Help', value: latestPatient.cahps_pain_symptom_help, target: '≥85' },
          { label: 'Emotional & Spiritual Support', value: latestPatient.cahps_emotional_spiritual_support, target: '≥85' },
          { label: 'Timeliness of Care', value: latestPatient.cahps_timeliness_of_care, target: '≥85' },
        ]);
      }

      // Staff Health Section
      if (latestStaff) {
        addSection('Staff Health', latestStaff, [
          { label: 'RN Turnover Rate', value: `${latestStaff.rn_turnover_rate_percent?.toFixed(1)}%`, target: '<20%' },
          { label: 'RN Vacancies', value: latestStaff.rn_vacancies, target: '0' },
          { label: 'Education Completion Rate', value: `${latestStaff.education_completion_rate_percent?.toFixed(1)}%`, target: '≥95%' },
          { label: 'Competency Review Completion', value: `${latestStaff.competency_review_completion_percent?.toFixed(1)}%`, target: '100%' },
        ]);
      }

      // PIPs Section
      if (reportData?.performance_improvement_projects?.length > 0) {
        if (yPos > pageHeight - 50) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(50, 50, 50);
        pdf.text('Performance Improvement Projects', margin, yPos);
        yPos += 8;

        reportData.performance_improvement_projects.forEach(pip => {
          if (yPos > pageHeight - 25) {
            pdf.addPage();
            yPos = margin;
          }
          
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text(`• ${pip.title}`, margin + 5, yPos);
          yPos += 5;
          
          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          pdf.text(`  Status: ${pip.status} | Category: ${pip.category} | Started: ${new Date(pip.start_date).toLocaleDateString()}`, margin + 5, yPos);
          yPos += 6;
        });
        yPos += 5;
      }

      // AI Insights Section
      if (reportData?.ai_insights?.length > 0) {
        if (yPos > pageHeight - 50) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(14);
        pdf.setTextColor(50, 50, 50);
        pdf.text('Recent AI Insights', margin, yPos);
        yPos += 8;

        reportData.ai_insights.slice(0, 5).forEach(insight => {
          if (yPos > pageHeight - 30) {
            pdf.addPage();
            yPos = margin;
          }
          
          pdf.setFontSize(10);
          pdf.setTextColor(60, 60, 60);
          pdf.text(`• [${insight.severity?.toUpperCase()}] ${insight.title}`, margin + 5, yPos);
          yPos += 5;
          
          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          const summaryLines = pdf.splitTextToSize(insight.summary || '', pageWidth - margin * 2 - 10);
          summaryLines.forEach(line => {
            if (yPos > pageHeight - 15) {
              pdf.addPage();
              yPos = margin;
            }
            pdf.text(`  ${line}`, margin + 5, yPos);
            yPos += 4;
          });
          yPos += 3;
        });
      }

      // Footer on each page
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
        pdf.text('QAPI Summary Report - Confidential', margin, pageHeight - 10);
      }

      // Save PDF
      const fileName = `qapi_report_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      toast.success('PDF exported successfully', { description: fileName });
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const exportWord = async () => {
    setExportingWord(true);
    try {
      const latestProgram = reportData?.program_overview_trend?.[0];
      const latestClinical = reportData?.clinical_metrics_trend?.[0];
      const latestSafety = reportData?.safety_incidents_trend?.[0];
      const latestRegulatory = reportData?.regulatory_compliance_trend?.[0];
      const latestPatient = reportData?.patient_experience_trend?.[0];
      const latestStaff = reportData?.staff_health_trend?.[0];

      const createTable = (headers, rows) => {
        return new DocxTable({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: [
            new DocxTableRow({
              children: headers.map(h => new DocxTableCell({
                children: [new Paragraph({ children: [new TextRun({ text: h, bold: true })] })],
                shading: { fill: "4F46E5" },
              }))
            }),
            ...rows.map(row => new DocxTableRow({
              children: row.map(cell => new DocxTableCell({
                children: [new Paragraph({ children: [new TextRun({ text: String(cell ?? '—') })] })]
              }))
            }))
          ]
        });
      };

      const sections = [];

      // Title
      sections.push(
        new Paragraph({
          children: [new TextRun({ text: "QAPI Summary Report", bold: true, size: 48, color: "4F46E5" })],
          heading: HeadingLevel.TITLE,
        }),
        new Paragraph({
          children: [new TextRun({ text: `Generated: ${new Date(reportData?.generated_at).toLocaleString()} by ${reportData?.generated_by || 'System'}`, size: 20, color: "666666" })]
        }),
        new Paragraph({ text: "" })
      );

      // Program Overview
      if (latestProgram) {
        sections.push(
          new Paragraph({ children: [new TextRun({ text: "Program Overview", bold: true, size: 28 })], heading: HeadingLevel.HEADING_1 }),
          createTable(
            ["Metric", "Value", "Target"],
            [
              ["Average Daily Census (ADC)", latestProgram.adc?.toFixed(1), ""],
              ["Total Admissions", latestProgram.total_admissions, ""],
              ["Live Discharges", `${latestProgram.live_discharges_percent?.toFixed(1)}%`, ""],
              ["Revocations", `${latestProgram.revocations_percent?.toFixed(1)}%`, "<5%"],
              ["Average Length of Stay", `${latestProgram.alos?.toFixed(1)} days`, ""],
              ["CAHPS Overall Score", latestProgram.cahps_overall_score?.toFixed(1), "≥85"],
            ]
          ),
          new Paragraph({ text: "" })
        );
      }

      // Clinical Quality
      if (latestClinical) {
        sections.push(
          new Paragraph({ children: [new TextRun({ text: "Clinical Quality Metrics", bold: true, size: 28 })], heading: HeadingLevel.HEADING_1 }),
          createTable(
            ["Metric", "Value", "Target"],
            [
              ["Pain Controlled Within 48h", `${latestClinical.pain_controlled_48h_percent?.toFixed(1)}%`, "≥95%"],
              ["Missed Visit Rate", `${latestClinical.missed_visit_rate_percent?.toFixed(1)}%`, "<5%"],
              ["HOPE Completed On Time", `${latestClinical.hope_completed_on_time_percent?.toFixed(1)}%`, "≥95%"],
              ["Timely IDG Completion", `${latestClinical.timely_idg_completion_percent?.toFixed(1)}%`, "100%"],
            ]
          ),
          new Paragraph({ text: "" })
        );
      }

      // Safety & Incidents
      if (latestSafety) {
        sections.push(
          new Paragraph({ children: [new TextRun({ text: "Safety & Incidents", bold: true, size: 28 })], heading: HeadingLevel.HEADING_1 }),
          createTable(
            ["Metric", "Value", "Target"],
            [
              ["Falls with Injury", latestSafety.falls_with_injury, "0"],
              ["Falls without Injury", latestSafety.falls_without_injury, ""],
              ["Medication Errors (Harm)", latestSafety.medication_errors_actual_harm, "0"],
              ["Hospitalizations", latestSafety.hospitalizations, ""],
              ["ER Visits", latestSafety.er_visits, ""],
            ]
          ),
          new Paragraph({ text: "" })
        );
      }

      // PIPs
      if (reportData?.performance_improvement_projects?.length > 0) {
        sections.push(
          new Paragraph({ children: [new TextRun({ text: "Performance Improvement Projects", bold: true, size: 28 })], heading: HeadingLevel.HEADING_1 })
        );
        reportData.performance_improvement_projects.forEach(pip => {
          sections.push(
            new Paragraph({
              children: [
                new TextRun({ text: `• ${pip.title}`, bold: true }),
                new TextRun({ text: ` - Status: ${pip.status}, Category: ${pip.category}`, italics: true, color: "666666" })
              ]
            })
          );
        });
        sections.push(new Paragraph({ text: "" }));
      }

      const doc = new Document({
        sections: [{
          properties: {},
          children: sections
        }]
      });

      const blob = await Packer.toBlob(doc);
      const fileName = `qapi_report_${new Date().toISOString().split('T')[0]}.docx`;
      saveAs(blob, fileName);
      toast.success('Word document exported successfully', { description: fileName });
    } catch (error) {
      console.error('Word export error:', error);
      toast.error('Failed to export Word document');
    } finally {
      setExportingWord(false);
    }
  };

  const generateManualReport = async (reportType) => {
    setGeneratingReport(true);
    try {
      const response = await fetch(`${API_URL}/api/reports/generate/${reportType}?format=pdf`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${reportType.charAt(0).toUpperCase() + reportType.slice(1)} report generated`, { 
          description: `Period: ${data.period_label}` 
        });
        fetchGeneratedReports();
      } else {
        const error = await response.json();
        toast.error('Failed to generate report', { description: error.detail });
      }
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setGeneratingReport(false);
    }
  };

  const createSchedule = async () => {
    try {
      const response = await fetch(`${API_URL}/api/reports/scheduled/config`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify(newSchedule)
      });
      
      if (response.ok) {
        toast.success('Schedule created successfully');
        setShowScheduleDialog(false);
        fetchScheduledConfigs();
      } else {
        const error = await response.json();
        toast.error('Failed to create schedule', { description: error.detail });
      }
    } catch (error) {
      toast.error('Failed to create schedule');
    }
  };

  const toggleSchedule = async (configId, enabled) => {
    try {
      await fetch(`${API_URL}/api/reports/scheduled/config/${configId}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}` 
        },
        body: JSON.stringify({ enabled })
      });
      fetchScheduledConfigs();
    } catch (error) {
      toast.error('Failed to update schedule');
    }
  };

  const latestProgram = reportData?.program_overview_trend?.[0];
  const latestClinical = reportData?.clinical_metrics_trend?.[0];
  const latestSafety = reportData?.safety_incidents_trend?.[0];
  const latestRegulatory = reportData?.regulatory_compliance_trend?.[0];
  const latestPatient = reportData?.patient_experience_trend?.[0];
  const latestStaff = reportData?.staff_health_trend?.[0];

  if (loading) {
    return (
      <div className="space-y-6" data-testid="reports-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-10 w-32" />
          </div>
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 no-print">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">QAPI Reports</h1>
          <p className="text-muted-foreground mt-1">Survey-ready quality reports</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handlePrint} data-testid="print-report-btn">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={exportPDF} disabled={exportingPDF} data-testid="export-pdf-btn">
            {exportingPDF ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileDown className="h-4 w-4 mr-2" />
                Export PDF
              </>
            )}
          </Button>
          <Button variant="outline" onClick={exportWord} disabled={exportingWord} data-testid="export-word-btn">
            {exportingWord ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <FileType className="h-4 w-4 mr-2" />
                Export Word
              </>
            )}
          </Button>
          <Button onClick={exportCSV} disabled={exporting} data-testid="export-csv-btn">
            <FileSpreadsheet className="h-4 w-4 mr-2" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
        </div>
      </div>

      {/* Automated Reports Section */}
      <Tabs defaultValue="enhanced" className="no-print">
        <TabsList>
          <TabsTrigger value="enhanced" data-testid="tab-enhanced">Enhanced Report</TabsTrigger>
          <TabsTrigger value="current">Current Report</TabsTrigger>
          <TabsTrigger value="scheduled">Scheduled Reports</TabsTrigger>
          <TabsTrigger value="history">Report History</TabsTrigger>
        </TabsList>

        {/* Enhanced Report Tab */}
        <TabsContent value="enhanced" className="space-y-4">
          <EnhancedReportSection 
            domain="all"
            title="Enhanced QAPI Report"
            description="Comprehensive metrics with custom timeframe and AI-powered insights"
            defaultTimeframe="30d"
          />
        </TabsContent>

        <TabsContent value="scheduled" className="space-y-4">
          <ScheduledReportManager />
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Generated Reports
              </CardTitle>
              <CardDescription>Previously generated monthly and quarterly reports</CardDescription>
            </CardHeader>
            <CardContent>
              {generatedReports.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No reports generated yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {generatedReports.map(report => (
                    <div key={report.id} className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-[#00b0ab]" />
                        <div>
                          <p className="font-medium">{report.period_label}</p>
                          <p className="text-sm text-muted-foreground">
                            {report.report_type || report.type} • {(report.format || 'pdf').toUpperCase()} • {new Date(report.generated_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <Badge>{report.report_type || report.type}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="current">

      {/* Report Header - Print Version */}
      <Card className="print:border-0 print:shadow-none">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">QAPI Summary Report</CardTitle>
              <CardDescription className="mt-2">
                <span className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Generated: {new Date(reportData?.generated_at).toLocaleString()}
                </span>
              </CardDescription>
            </div>
            <Badge variant="outline" className="text-lg px-4 py-2">
              Survey Ready
            </Badge>
          </div>
        </CardHeader>
      </Card>

      {/* Report Content */}
      <Tabs defaultValue="summary" className="space-y-6 no-print">
        <TabsList>
          <TabsTrigger value="summary" data-testid="tab-summary">Executive Summary</TabsTrigger>
          <TabsTrigger value="clinical" data-testid="tab-clinical">Clinical Quality</TabsTrigger>
          <TabsTrigger value="safety" data-testid="tab-safety">Safety & Compliance</TabsTrigger>
          <TabsTrigger value="pips" data-testid="tab-pips">PIPs</TabsTrigger>
          <TabsTrigger value="insights" data-testid="tab-insights">AI Insights</TabsTrigger>
        </TabsList>

        <TabsContent value="summary">
          <Card>
            <CardContent className="pt-6">
              <ReportSection title="Program Overview" icon={Activity}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow label="Average Daily Census (ADC)" value={latestProgram?.adc?.toFixed(1)} />
                    <MetricRow label="Total Admissions" value={latestProgram?.total_admissions} />
                    <MetricRow label="Live Discharges" value={`${latestProgram?.live_discharges_percent?.toFixed(1)}%`} />
                    <MetricRow label="Revocations" value={`${latestProgram?.revocations_percent?.toFixed(1)}%`} target="<5%" />
                    <MetricRow label="Average Length of Stay" value={`${latestProgram?.alos?.toFixed(1)} days`} />
                    <MetricRow label="CAHPS Overall Score" value={latestProgram?.cahps_overall_score?.toFixed(1)} target="≥85" />
                    <MetricRow label="Open Complaints" value={latestProgram?.open_complaints} target="0" />
                  </TableBody>
                </Table>
              </ReportSection>

              <ReportSection title="Patient Experience (CAHPS)" icon={TrendingUp}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Domain</TableHead>
                      <TableHead>Score</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow label="Communication" value={latestPatient?.cahps_communication} target="≥85" />
                    <MetricRow label="Pain & Symptom Help" value={latestPatient?.cahps_pain_symptom_help} target="≥85" />
                    <MetricRow label="Emotional & Spiritual Support" value={latestPatient?.cahps_emotional_spiritual_support} target="≥85" />
                    <MetricRow label="Timeliness of Care" value={latestPatient?.cahps_timeliness_of_care} target="≥85" />
                  </TableBody>
                </Table>
              </ReportSection>

              <ReportSection title="Staff Health" icon={Activity}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow 
                      label="RN Turnover Rate" 
                      value={`${latestStaff?.rn_turnover_rate_percent?.toFixed(1)}%`} 
                      target="<20%"
                      status={latestStaff?.rn_turnover_rate_percent > 20 ? 'danger' : 'good'}
                    />
                    <MetricRow label="RN Vacancies" value={latestStaff?.rn_vacancies} target="0" />
                    <MetricRow label="Education Completion" value={`${latestStaff?.education_completion_rate_percent?.toFixed(1)}%`} target="≥95%" />
                    <MetricRow label="Competency Reviews" value={`${latestStaff?.competency_review_completion_percent?.toFixed(1)}%`} target="100%" />
                  </TableBody>
                </Table>
              </ReportSection>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="clinical">
          <Card>
            <CardContent className="pt-6">
              <ReportSection title="Symptom Management" icon={Activity}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow 
                      label="Pain Controlled Within 48h" 
                      value={`${latestClinical?.pain_controlled_48h_percent?.toFixed(1)}%`} 
                      target="≥95%"
                      status={latestClinical?.pain_controlled_48h_percent >= 95 ? 'good' : 'warning'}
                    />
                    <MetricRow 
                      label="Dyspnea Improvement Within 48h" 
                      value={`${latestClinical?.dyspnea_improvement_48h_percent?.toFixed(1)}%`} 
                      target="≥90%"
                    />
                    <MetricRow 
                      label="Anxiety/Agitation Improvement" 
                      value={`${latestClinical?.anxiety_improvement_percent?.toFixed(1)}%`} 
                    />
                    <MetricRow 
                      label="Uncontrolled Symptom Escalations" 
                      value={latestClinical?.uncontrolled_symptom_escalations} 
                      target="0"
                      status={latestClinical?.uncontrolled_symptom_escalations > 0 ? 'warning' : 'good'}
                    />
                  </TableBody>
                </Table>
              </ReportSection>

              <ReportSection title="Visits & Care Delivery" icon={Activity}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow 
                      label="Visits Completed vs Planned" 
                      value={`${latestClinical?.visits_completed_vs_planned_percent?.toFixed(1)}%`} 
                      target="≥95%"
                    />
                    <MetricRow 
                      label="Missed Visit Rate" 
                      value={`${latestClinical?.missed_visit_rate_percent?.toFixed(1)}%`} 
                      target="<5%"
                      status={latestClinical?.missed_visit_rate_percent > 5 ? 'danger' : 'good'}
                    />
                    <MetricRow 
                      label="Avg Visits per RN per Day" 
                      value={latestClinical?.avg_visits_per_rn_per_day?.toFixed(1)} 
                    />
                    <MetricRow 
                      label="LPN Utilization Rate" 
                      value={`${latestClinical?.lpn_utilization_rate_percent?.toFixed(1)}%`} 
                    />
                  </TableBody>
                </Table>
              </ReportSection>

              <ReportSection title="HOPE / Clinical Compliance" icon={Activity}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow 
                      label="HOPE Completed On Time" 
                      value={`${latestClinical?.hope_completed_on_time_percent?.toFixed(1)}%`} 
                      target="≥95%"
                    />
                    <MetricRow 
                      label="SFV Triggered Appropriately" 
                      value={`${latestClinical?.sfv_triggered_appropriately_percent?.toFixed(1)}%`} 
                    />
                    <MetricRow 
                      label="Moderate-Severe Follow-ups" 
                      value={`${latestClinical?.moderate_severe_followups_completed_percent?.toFixed(1)}%`} 
                      target="100%"
                    />
                    <MetricRow 
                      label="Timely IDG Completion" 
                      value={`${latestClinical?.timely_idg_completion_percent?.toFixed(1)}%`} 
                      target="100%"
                    />
                  </TableBody>
                </Table>
              </ReportSection>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="safety">
          <Card>
            <CardContent className="pt-6">
              <ReportSection title="Safety & Incidents" icon={AlertTriangle}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Count</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow 
                      label="Falls with Injury" 
                      value={latestSafety?.falls_with_injury} 
                      target="0"
                      status={latestSafety?.falls_with_injury > 0 ? 'danger' : 'good'}
                    />
                    <MetricRow label="Falls without Injury" value={latestSafety?.falls_without_injury} />
                    <MetricRow 
                      label="Medication Errors (Actual Harm)" 
                      value={latestSafety?.medication_errors_actual_harm} 
                      target="0"
                      status={latestSafety?.medication_errors_actual_harm > 0 ? 'danger' : 'good'}
                    />
                    <MetricRow label="Medication Errors (Near Miss)" value={latestSafety?.medication_errors_near_miss} />
                    <MetricRow label="Hospitalizations" value={latestSafety?.hospitalizations} />
                    <MetricRow label="ER Visits" value={latestSafety?.er_visits} />
                    <MetricRow label="After-Hours Escalations" value={latestSafety?.after_hours_escalations} />
                    <MetricRow label="Level 3-5 Incidents" value={
                      (latestSafety?.level_3_incidents || 0) + 
                      (latestSafety?.level_4_incidents || 0) + 
                      (latestSafety?.level_5_incidents || 0)
                    } />
                    <MetricRow 
                      label="RCA Completed" 
                      value={`${latestSafety?.rca_completed_percent?.toFixed(1)}%`} 
                      target="100%"
                    />
                  </TableBody>
                </Table>
              </ReportSection>

              <ReportSection title="Regulatory Compliance" icon={FileText}>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Metric</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <MetricRow 
                      label="CTI Timeliness" 
                      value={`${latestRegulatory?.cti_timeliness_percent?.toFixed(1)}%`} 
                      target="100%"
                    />
                    <MetricRow 
                      label="F2F Correct Window" 
                      value={`${latestRegulatory?.f2f_correct_window_percent?.toFixed(1)}%`} 
                      target="100%"
                    />
                    <MetricRow 
                      label="POC Updates On Time" 
                      value={`${latestRegulatory?.poc_updates_on_time_percent?.toFixed(1)}%`} 
                      target="≥98%"
                    />
                    <MetricRow 
                      label="Orders Signed On Time" 
                      value={`${latestRegulatory?.orders_signed_on_time_percent?.toFixed(1)}%`} 
                      target="≥95%"
                    />
                    <MetricRow 
                      label="Documentation Completeness" 
                      value={`${latestRegulatory?.documentation_completeness_score?.toFixed(1)}%`} 
                      target="≥95%"
                    />
                    <MetricRow 
                      label="ADR Favorable" 
                      value={latestRegulatory?.adr_favorable} 
                    />
                    <MetricRow 
                      label="ADR Unfavorable" 
                      value={latestRegulatory?.adr_unfavorable} 
                      target="0"
                      status={latestRegulatory?.adr_unfavorable > 0 ? 'danger' : 'good'}
                    />
                  </TableBody>
                </Table>
              </ReportSection>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pips">
          <Card>
            <CardContent className="pt-6">
              <ReportSection title="Performance Improvement Projects" icon={ClipboardList}>
                {reportData?.performance_improvement_projects?.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Start Date</TableHead>
                        <TableHead>Target</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.performance_improvement_projects.map(pip => (
                        <TableRow key={pip.id}>
                          <TableCell className="font-medium">{pip.title}</TableCell>
                          <TableCell>{pip.category}</TableCell>
                          <TableCell>
                            <Badge variant={
                              pip.status === 'Active' ? 'default' :
                              pip.status === 'Improving' ? 'secondary' :
                              pip.status === 'Sustained' ? 'outline' : 'secondary'
                            }>
                              {pip.status}
                            </Badge>
                          </TableCell>
                          <TableCell>{new Date(pip.start_date).toLocaleDateString()}</TableCell>
                          <TableCell>{pip.target_benchmark || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No PIPs on record</p>
                )}
              </ReportSection>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="insights">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between mb-4 pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-primary" />
                  <h2 className="text-lg font-semibold">Recent AI Insights</h2>
                </div>
                <InsightsExport 
                  insights={(reportData?.ai_insights || []).map(i => ({
                    title: i.title,
                    description: i.summary || i.description,
                    severity: i.severity,
                    category: i.insight_type || i.category,
                    recommendation: i.recommended_actions?.join('; ')
                  }))} 
                  pageTitle="QAPI Reports" 
                />
              </div>
              {reportData?.ai_insights?.length > 0 ? (
                  <ScrollArea className="h-96">
                    <div className="space-y-4">
                      {reportData.ai_insights.map(insight => (
                        <div key={insight.id} className="p-4 rounded-lg border">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant={
                              insight.severity === 'critical' ? 'destructive' :
                              insight.severity === 'high' ? 'default' : 'secondary'
                            }>
                              {insight.severity}
                            </Badge>
                            <Badge variant="outline">{insight.insight_type}</Badge>
                          </div>
                          <h4 className="font-semibold">{insight.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1">{insight.summary}</p>
                          {insight.recommended_actions?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-medium">Recommended Actions:</p>
                              <ul className="text-xs text-muted-foreground list-disc list-inside">
                                {insight.recommended_actions.slice(0, 3).map((action, i) => (
                                  <li key={i}>{action}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                ) : (
                  <p className="text-muted-foreground text-center py-8">No insights generated yet</p>
                )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Print-only full report */}
      <div className="hidden print:block">
        <ReportSection title="Clinical Quality Metrics" icon={Activity}>
          <p className="text-sm mb-4">Period: {latestClinical?.period_date}</p>
          <Table>
            <TableBody>
              <MetricRow label="Pain Controlled Within 48h" value={`${latestClinical?.pain_controlled_48h_percent?.toFixed(1)}%`} target="≥95%" />
              <MetricRow label="Missed Visit Rate" value={`${latestClinical?.missed_visit_rate_percent?.toFixed(1)}%`} target="<5%" />
              <MetricRow label="HOPE Completed On Time" value={`${latestClinical?.hope_completed_on_time_percent?.toFixed(1)}%`} target="≥95%" />
            </TableBody>
          </Table>
        </ReportSection>

        <ReportSection title="Safety & Incidents" icon={AlertTriangle}>
          <Table>
            <TableBody>
              <MetricRow label="Falls with Injury" value={latestSafety?.falls_with_injury} />
              <MetricRow label="Medication Errors (Harm)" value={latestSafety?.medication_errors_actual_harm} />
              <MetricRow label="RCA Completed" value={`${latestSafety?.rca_completed_percent?.toFixed(1)}%`} />
            </TableBody>
          </Table>
        </ReportSection>
      </div>
        </TabsContent>
      </Tabs>

      {/* Schedule Dialog */}
      <Dialog open={showScheduleDialog} onOpenChange={setShowScheduleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Report Schedule</DialogTitle>
            <DialogDescription>
              Configure automatic report generation on a monthly or quarterly basis
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Report Type</Label>
              <Select 
                value={newSchedule.report_type}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, report_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Day of Month to Generate</Label>
              <Select 
                value={String(newSchedule.day_of_month)}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, day_of_month: parseInt(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 5, 10, 15, 20, 25, 28].map(day => (
                    <SelectItem key={day} value={String(day)}>Day {day}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Export Format</Label>
              <Select 
                value={newSchedule.format}
                onValueChange={(value) => setNewSchedule({ ...newSchedule, format: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="word">Word Document</SelectItem>
                  <SelectItem value="both">Both PDF & Word</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowScheduleDialog(false)}>Cancel</Button>
            <Button onClick={createSchedule}>Create Schedule</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
